import { useState, useMemo, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import z from "zod";

import Typography from "core/components/Typography";
import Field from "core/components/Field";
import Button from "core/components/Button";
import Flex from "core/components/Flex";
import useCurrentUser from "core/hooks/useCurrentUser";
import useAuthenticatedMutation from "core/hooks/useAuthenticatedMutation";
import { updateUserProfileByUserId } from "modules/admin/actions";

const profileSchema = z.object({
  userId: z.string().min(1),
  name: z.string().min(2, { message: "Must be at least 2 characters long." }),
  alternate_email: z.union([z.email(), z.literal("")]),
});

const useFormState = ({ defaultState, schema }) => {
  const [values, setValues] = useState(defaultState);
  const [touchedState, setTouchedState] = useState({});

  const { success: isValid, data, error } = schema.safeParse(values);
  const fieldErrors = useMemo(() => {
    return error?.flatten().fieldErrors ?? {};
  }, [error]);

  const getFieldValue = useCallback(
    (name) => {
      return values[name];
    },
    [values]
  );

  const onFieldChange = useCallback((name) => {
    return (value) => {
      setValues((prev) => {
        return {
          ...prev,
          [name]: value,
        };
      });
    };
  }, []);

  const getFieldError = useCallback(
    (name) => {
      return fieldErrors[name];
    },
    [fieldErrors]
  );

  const getFieldTouched = useCallback(
    (name) => {
      return touchedState[name] ?? false;
    },
    [touchedState]
  );

  const onBlurTouched = useCallback((name) => {
    return () => {
      setTouchedState((prev) => {
        return {
          ...prev,
          [name]: true,
        };
      });
    };
  }, []);

  const resetForm = useCallback(() => {
    setValues(defaultState);
    setTouchedState({});
  }, []);

  return {
    isValid,
    validFormValues: data,
    getFieldValue,
    onFieldChange,
    getFieldError,
    getFieldTouched,
    onBlurTouched,
    resetForm,
  };
};

const ProfileForm = () => {
  const { currentUser } = useCurrentUser();
  const queryClient = useQueryClient();

  const {
    isValid,
    validFormValues,
    getFieldValue,
    onFieldChange,
    getFieldError,
    getFieldTouched,
    onBlurTouched,
    resetForm,
  } = useFormState({
    defaultState: {
      userId: currentUser?.userId ?? "",
      name: currentUser?.name ?? "",
      alternate_email: currentUser?.alternate_email ?? "",
    },
    schema: profileSchema,
  });

  const updateMutationFn = useAuthenticatedMutation(updateUserProfileByUserId);
  const { mutate: updateProfile, isPending: isSaving } = useMutation({
    mutationFn: updateMutationFn,
    onSuccess: () => {
      resetForm();
      queryClient.invalidateQueries({
        queryKey: ["user-profiles", { userId: currentUser?.userId }],
      });
    },
  });

  const handleSubmit = () => {
    updateProfile({
      userId: validFormValues.userId,
      profile: {
        name: validFormValues.name,
        alternate_email: validFormValues.alternate_email || null,
      },
    });
  };

  if (!currentUser) return null;

  return (
    <Flex
      direction="row"
      justifyContent="center"
      alignItems="flex-start"
      height="100dvh"
      style={{ paddingTop: "20dvh" }}
    >
      <Flex direction="column" width="405px">
        <Typography variant="h3" style={{ marginBottom: "24px" }}>
          Complete your Populi profile
        </Typography>

        <Flex direction="column" gap="4px">
          <Field
            id="name"
            type="text"
            labelText="Name"
            isRequired
            isDisabled={isSaving}
            error={getFieldTouched("name") && getFieldError("name")}
            value={getFieldValue("name")}
            onChange={onFieldChange("name")}
            onBlur={onBlurTouched("name")}
          />

          <Field
            id="alternate_email"
            type="email"
            labelText="Alternate Email"
            isDisabled={isSaving}
            error={
              getFieldTouched("alternate_email") &&
              getFieldError("alternate_email")
            }
            value={getFieldValue("alternate_email")}
            onChange={onFieldChange("alternate_email")}
            onBlur={onBlurTouched("alternate_email")}
          />

          <Flex direction="row" gap="16px" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!isValid}
              isLoading={isSaving}
            >
              Complete Profile
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ProfileForm;
